import logoUrl from '../assets/edlio-logo.svg';
import { CHATBOT, HOST_ATTR } from './contract';
import { inkFor, normalizeHex } from './color';
import {
  ALIGN_ICONS,
  CHECK_ICON,
  CLOSE_ICON,
  CONTRAST_ICONS,
  HEADER_ICON,
  LAUNCHER_ICON,
  PAUSE_ICON,
  PLAY_ICON,
  RESET_ICON,
  SATURATION_ICONS,
  SETTING_ICONS,
  SPEAKER_ICON,
  STATEMENT_ICON,
  STOP_ICON,
  TEXT_SIZE_ICON,
} from './icons';
import {
  BOOLEAN_PREFS,
  DEFAULT_PREFS,
  effectiveMotion,
  SPEECH_RATES,
  type BooleanPref,
  type Prefs,
} from './prefs';
import { tick } from './sound';
import {
  itemsFrom,
  pageItems,
  pointItem,
  Reader,
  READING_ATTR,
  selectionItems,
  speechSupported,
} from './speech';
import { readStatement, renderStatement } from './statement';
import { STRINGS } from './strings';
import { WIDGET_CSS } from './styles';
import { INTERACTIVE } from './text-blocks';
import {
  CYCLES,
  isCycle,
  nextValue,
  stepCount,
  stepOf,
  TILE_ORDER,
  type CycleKey,
  type TileKey,
} from './tiles';
import type { Environment } from './apply';

/**
 * The logo's URL, made absolute against this bundle's own URL. webpack writes it root-relative
 * (publicPath `/apps/webapps/next/modern-ui/pages/`), which is right on a CMS page but points at
 * the school's own host when the standalone loader puts the widget on a non-Edlio website
 * (edlio/cms#16832). Read while the bundle executes, the only time currentScript is set. On a CMS
 * page the bundle is same-origin, so the URL is unchanged there.
 */
const LOGO_SRC: string = (() => {
  try {
    const script = document.currentScript;
    if (script instanceof HTMLScriptElement && script.src)
      return new URL(logoUrl, script.src).href;
  } catch {
    /* keep the root-relative URL */
  }
  return logoUrl;
})();

/** Where the footer's Edlio logo goes. Absolute: the widget runs on school hosts, not ours. */
const BRAND_URL = 'https://www.edlio.com/school-ada-compliance';

type SwitchKey = BooleanPref | 'motion';

/** Launcher size (px). Keep in step with `.launcher` in styles.ts. */
const LAUNCHER = 56;
/** Gap between the launcher and the page edge, or the chatbot's launcher. */
const EDGE = 20;
const GAP = 12;
/** Read aloud player height (px). Keep in step with `.reader` in styles.ts. */
const READER = 48;
/** How long the pointer rests on something before it is read. */
const POINT_DWELL_MS = 450;
/** How long the pointer rests on a tile before its tip shows. */
const TIP_DELAY_MS = 450;

/** Keyboard focus, not a click. Older browsers without :focus-visible count every focus. */
function focusVisible(el: HTMLElement): boolean {
  try {
    return el.matches(':focus-visible');
  } catch (e) {
    return true;
  }
}

/**
 * The launcher button, the preferences panel and the statement dialog, in a
 * Shadow DOM on the server-emitted host <div>. Ticket §7 lists the
 * accessibility rules this follows; the short version:
 *
 *   - launcher: <button>, aria-expanded, aria-controls
 *   - panel: non-modal role="dialog", labelled by its heading; opening moves
 *     focus to the heading, Esc closes and returns focus to the launcher,
 *     a click outside closes without moving focus
 *   - every setting is a tile in one grid. On/off settings are
 *     role="switch" buttons; settings with steps are plain buttons whose
 *     label names the current step, and each change is announced
 *   - statement: modal role="dialog" with aria-modal; focus moves in, Tab
 *     stays inside, Esc or the close button returns focus to the opener
 *   - read aloud: a labelled region beside the launcher with real buttons;
 *     it never starts by itself, and Esc anywhere on the page stops it
 *
 * Every string in the markup is a constant from strings.ts. The only values
 * that come from the page are the host's data attributes and the statement
 * text, and those are set as properties or text nodes, never as HTML.
 */
export class Widget {
  private readonly host: HTMLElement;
  private readonly onChange: (next: Prefs) => void;
  private shadow!: ShadowRoot;
  private root!: HTMLElement;
  private launcher!: HTMLButtonElement;
  private panel!: HTMLElement;
  private heading!: HTMLElement;
  private dot!: HTMLElement;
  private status!: HTMLElement;
  private guide!: HTMLElement;
  private logo!: HTMLImageElement;
  private layer!: HTMLElement;
  private modal!: HTMLElement;
  private modalTitle!: HTMLElement;
  private modalBody!: HTMLElement;
  private statementButton!: HTMLButtonElement;
  private statementText: string | null = null;
  private reader: Reader | null = null;
  private readerBar!: HTMLElement;
  private readerPlay!: HTMLButtonElement;
  private readerStop!: HTMLButtonElement;
  private readerRate!: HTMLButtonElement;
  private readerMsg!: HTMLElement;
  private speechOn = false;
  private hasSelection = false;
  /** The selection as it was when the Read button was pressed. */
  private pressedSelection: ReturnType<typeof selectionItems> = null;
  private reading: HTMLElement | null = null;
  /** A pointerdown outside just closed the panel: its click is not a "read this". */
  private swallowClick = false;
  /** The visitor just turned read aloud on from the panel: start reading. */
  private startReading = false;
  /** The browser refused to start before a click; the next click starts it. */
  private waitingForClick = false;
  private pointTimer = 0;
  private lastPointed: HTMLElement | null = null;
  private readerHint!: HTMLElement;
  private mounted = false;
  private tip!: HTMLElement;
  private tipTimer = 0;
  /** The launcher was pressed with a pointer, so the heading takes focus without a ring. */
  private pointerOpen = false;
  private prefs: Prefs = { ...DEFAULT_PREFS };
  private env: Environment = { deviceReducesMotion: false };
  private isOpen = false;
  private isModalOpen = false;
  private guideListening = false;
  private wideQuery: MediaQueryList | null = null;

  constructor(host: HTMLElement, onChange: (next: Prefs) => void) {
    this.host = host;
    this.onChange = onChange;
  }

  /** False when the host already has a widget (the bundle loaded twice). */
  mount(prefs: Prefs, env: Environment): boolean {
    if (this.host.shadowRoot || typeof this.host.attachShadow !== 'function')
      return false;

    this.pinHost();
    const shadow = this.host.attachShadow({ mode: 'open' });
    shadow.innerHTML = `<style>${WIDGET_CSS}</style>${template()}`;
    this.shadow = shadow;

    const q = <T extends HTMLElement>(selector: string) =>
      shadow.querySelector(selector) as T;
    this.root = q('.root');
    this.launcher = q('.launcher');
    this.panel = q('.panel');
    this.heading = q('#ea11y-title');
    this.dot = q('.dot');
    this.status = q('[role="status"]');
    this.guide = q('.guide');
    this.logo = q('.logo');
    this.layer = q('.layer');
    this.modal = q('.modal');
    this.modalTitle = q('#ea11y-statement-title');
    this.modalBody = q('.modal-body');
    this.statementButton = q('.statement-open');
    this.readerBar = q('.reader');
    this.readerPlay = q('.reader-play');
    this.readerStop = q('.reader-stop');
    this.readerRate = q('.reader-rate');
    this.readerMsg = q('.reader-msg');
    this.readerHint = q('.reader-hint');
    this.tip = q('.tip');

    const color = normalizeHex(this.host.getAttribute(HOST_ATTR.color));
    this.root.style.setProperty('--ea11y-bg', color);
    this.root.style.setProperty('--ea11y-fg', inkFor(color));

    // The statement Edlio writes opens here; a school's own page is a link.
    this.statementText = readStatement(this.host);
    const statementUrl = safeLink(
      this.host.getAttribute(HOST_ATTR.statementUrl) || '',
    );
    if (this.statementText) {
      this.statementButton.hidden = false;
    } else if (statementUrl) {
      const link = q<HTMLAnchorElement>('.statement-link');
      link.href = statementUrl;
      link.hidden = false;
    }

    this.layout();
    if (typeof window.matchMedia === 'function') {
      this.wideQuery = window.matchMedia(CHATBOT.wideQuery);
      const relayout = () => this.layout();
      if (typeof this.wideQuery.addEventListener === 'function') {
        this.wideQuery.addEventListener('change', relayout);
      } else if (typeof this.wideQuery.addListener === 'function') {
        this.wideQuery.addListener(relayout); // Safari 13
      }
    }

    this.wire(shadow);
    this.setupReader();
    this.update(prefs, env);
    this.mounted = true;
    return true;
  }

  update(prefs: Prefs, env: Environment): void {
    this.prefs = prefs;
    this.env = env;
    const shadow = this.host.shadowRoot;
    if (!shadow) return;

    shadow
      .querySelectorAll<HTMLElement>('.cycle')
      .forEach((tile) =>
        drawCycle(tile, tile.getAttribute('data-pref') as CycleKey, prefs),
      );

    const motion = effectiveMotion(prefs, env.deviceReducesMotion);
    shadow.querySelectorAll<HTMLElement>('.switch').forEach((button) => {
      const key = button.getAttribute('data-pref') as SwitchKey;
      const on = key === 'motion' ? motion : prefs[key];
      button.setAttribute('aria-checked', on ? 'true' : 'false');
      button.classList.toggle('on', on);
    });

    const speech = prefs.speech && !!this.reader;
    const anyOn =
      (Object.keys(CYCLES) as CycleKey[]).some(
        (key) => stepOf(key, prefs[key]) > 0,
      ) ||
      motion ||
      BOOLEAN_PREFS.some((key) => (key === 'speech' ? speech : prefs[key]));
    this.dot.hidden = !anyOn;
    // The page is inverted under <html>'s filter; the widget turns itself back.
    this.root.classList.toggle('inverted', prefs.contrast === 'invert');
    // Low and high saturation can be undone on the widget; desaturate cannot.
    this.root.classList.toggle('sat-low', prefs.saturation === 'low');
    this.root.classList.toggle('sat-high', prefs.saturation === 'high');
    this.root.classList.toggle('big-cursor', prefs.cursor);
    this.setGuide(prefs.guide);
    this.setSpeech(speech);
    if (this.reader) this.reader.setRate(prefs.speechRate);
    const rate = `${prefs.speechRate}\u00d7`;
    this.readerRate.setAttribute('aria-label', `${STRINGS.readSpeed} ${rate}`);
    (this.readerRate.querySelector('.rate-value') as HTMLElement).textContent =
      rate;
  }

  // ---- host ----------------------------------------------------------------

  /**
   * Inline !important styles, because the host is an ordinary <div> in the
   * school's page and page CSS beats :host rules. Nothing a design does to
   * `div` can move, hide or resize it. The corner offsets come from layout().
   */
  private pinHost(): void {
    const s = this.host.style;
    const pin = (prop: string, value: string) =>
      s.setProperty(prop, value, 'important');
    pin('position', 'fixed');
    pin('top', 'auto');
    pin('z-index', '2147483000');
    pin('display', 'block');
    pin('width', 'auto');
    pin('height', 'auto');
    pin('margin', '0');
    pin('padding', '0');
    pin('border', '0');
    pin('background', 'none');
    pin('transform', 'none');
    pin('filter', 'none');
    pin('opacity', '1');
    pin('visibility', 'visible');
  }

  /**
   * Corner placement. When the public chatbot sits in the same corner, the
   * launcher moves in beside it, vertically centred on the chatbot's button,
   * rather than on top of it: the chatbot's greeting bubble opens straight
   * above its button, so stacking would put ours under the bubble.
   */
  private layout(): void {
    const side =
      this.host.getAttribute(HOST_ATTR.position) === 'right' ? 'right' : 'left';
    const beside = chatbotSide(document) === side;
    const wide = this.wideQuery ? this.wideQuery.matches : true;
    const bottom = beside
      ? (wide ? CHATBOT.bottomWide : CHATBOT.bottomNarrow) +
        (CHATBOT.size - LAUNCHER) / 2
      : EDGE;
    const inset = beside ? CHATBOT.inset + CHATBOT.size + GAP : EDGE;

    const s = this.host.style;
    s.setProperty('bottom', `${bottom}px`, 'important');
    s.setProperty(side, `${inset}px`, 'important');
    s.setProperty(side === 'right' ? 'left' : 'right', 'auto', 'important');
    this.host.setAttribute('data-beside-chatbot', beside ? 'true' : 'false');
    // On wide screens the panel lines up with the launcher's bottom edge.
    this.root.style.setProperty('--ea11y-host-bottom', `${bottom}px`);
    // The phone-width sheet is position: fixed, so it needs the real offset.
    this.root.style.setProperty(
      '--ea11y-sheet-bottom',
      `${bottom + LAUNCHER + GAP}px`,
    );
    // The read aloud player sits beside the launcher, centred on it.
    this.root.style.setProperty(
      '--ea11y-reader-inset',
      `${inset + LAUNCHER + GAP}px`,
    );
    this.root.style.setProperty(
      '--ea11y-reader-bottom',
      `${bottom + (LAUNCHER - READER) / 2}px`,
    );
  }

  // ---- events --------------------------------------------------------------

  private wire(shadow: ShadowRoot): void {
    this.launcher.addEventListener('pointerdown', () => {
      this.pointerOpen = true;
    });
    this.launcher.addEventListener('keydown', () => {
      this.pointerOpen = false;
    });
    this.launcher.addEventListener('click', () =>
      this.isOpen ? this.close(true) : this.open(),
    );
    (shadow.querySelector('.close') as HTMLElement).addEventListener(
      'click',
      () => this.close(true),
    );
    this.statementButton.addEventListener('click', () => this.openModal());
    (shadow.querySelector('.modal-close') as HTMLElement).addEventListener(
      'click',
      () => this.closeModal(),
    );
    // A click on the dimmed backdrop (not the dialog) closes it.
    this.layer.addEventListener('click', (event) => {
      if (event.target === this.layer) this.closeModal();
    });

    shadow.addEventListener('keydown', (event) => {
      const e = event as KeyboardEvent;
      if (e.key === 'Escape' || e.key === 'Esc') {
        if (!this.tip.hidden) {
          // A tip goes first, without moving focus (WCAG 1.4.13).
          e.stopPropagation();
          this.hideTip();
        } else if (
          this.reader &&
          this.reader.state !== 'idle' &&
          this.readerBar.contains(e.target as Node)
        ) {
          e.stopPropagation();
          this.reader.stop();
        } else if (this.isModalOpen) {
          e.stopPropagation();
          this.closeModal();
        } else if (this.isOpen) {
          e.stopPropagation();
          this.close(true);
        }
      } else if (e.key === 'Tab' && this.isModalOpen) {
        this.trapTab(e);
      }
    });

    // Outside click closes, without pulling focus back to the launcher.
    document.addEventListener(
      'pointerdown',
      (event) => {
        this.swallowClick = false;
        if (!this.isOpen) return;
        const path =
          typeof event.composedPath === 'function' ? event.composedPath() : [];
        if (path.indexOf(this.host) === -1) {
          this.close(false);
          this.swallowClick = true;
        }
      },
      true,
    );

    shadow.querySelectorAll<HTMLElement>('.cycle').forEach((tile) =>
      tile.addEventListener('click', () => {
        const key = tile.getAttribute('data-pref') as CycleKey;
        const value = nextValue(key, this.prefs[key]);
        this.emit({ ...this.prefs, [key]: value } as Prefs);
        const step = stepOf(key, value);
        const copy = STRINGS.cycles[key];
        this.announce(
          step ? copy.steps[step - 1] : `${copy.name} ${STRINGS.off}`,
        );
      }),
    );

    shadow.querySelectorAll<HTMLElement>('.switch').forEach((button) =>
      button.addEventListener('click', () => {
        const key = button.getAttribute('data-pref') as SwitchKey;
        if (key === 'motion') {
          // Store an explicit choice: the opposite of what is in effect now.
          this.emit({
            ...this.prefs,
            motion: !effectiveMotion(this.prefs, this.env.deviceReducesMotion),
          });
        } else if (key === 'speech' && !this.prefs.speech) {
          // Turning read aloud on starts reading, so get the panel out of the
          // way of the page and put focus on the player's Pause button.
          this.startReading = true;
          this.emit({ ...this.prefs, speech: true });
          if (this.speechOn) {
            this.close(false);
            this.readerPlay.focus();
          }
        } else {
          this.emit({ ...this.prefs, [key]: !this.prefs[key] } as Prefs);
        }
      }),
    );

    // Tips: a line of help for each tile, on hover and on keyboard focus.
    shadow.querySelectorAll<HTMLElement>('.tile').forEach((tile) => {
      tile.addEventListener('mouseenter', () => this.showTipSoon(tile));
      tile.addEventListener('mouseleave', () => this.hideTipSoon());
      tile.addEventListener('focus', () => {
        if (focusVisible(tile)) this.showTip(tile);
      });
      tile.addEventListener('blur', () => this.hideTip());
      tile.addEventListener('click', () => this.hideTip());
    });
    // The tip can be hovered without it disappearing (WCAG 1.4.13).
    this.tip.addEventListener('mouseenter', () => {
      if (this.tipTimer) window.clearTimeout(this.tipTimer);
      this.tipTimer = 0;
    });
    this.tip.addEventListener('mouseleave', () => this.hideTipSoon());
    this.panel.addEventListener('scroll', () => this.hideTip(), {
      passive: true,
    });

    (shadow.querySelector('.reset') as HTMLElement).addEventListener(
      'click',
      () => {
        this.emit({ ...DEFAULT_PREFS });
        this.announce(STRINGS.resetDone);
      },
    );
  }

  private emit(next: Prefs): void {
    this.onChange(next);
  }

  /** Says something through the panel's live region. */
  private announce(text: string): void {
    // Clear first so the same words are announced again on a second press.
    this.status.textContent = '';
    window.setTimeout(() => {
      this.status.textContent = text;
    }, 50);
  }

  private open(): void {
    // The logo is only fetched once someone opens the panel.
    if (!this.logo.getAttribute('src')) this.logo.setAttribute('src', LOGO_SRC);
    this.isOpen = true;
    this.panel.hidden = false;
    this.launcher.setAttribute('aria-expanded', 'true');
    // Focus still moves to the heading; only the ring is skipped for a click.
    this.heading.classList.toggle('quiet', this.pointerOpen);
    this.pointerOpen = false;
    this.heading.focus();
  }

  private close(returnFocus: boolean): void {
    this.hideTip();
    if (this.isModalOpen) this.closeModal(false);
    this.isOpen = false;
    this.panel.hidden = true;
    this.launcher.setAttribute('aria-expanded', 'false');
    if (returnFocus) this.launcher.focus();
  }

  // ---- tips ----------------------------------------------------------------

  private showTipSoon(tile: HTMLElement): void {
    if (this.tipTimer) window.clearTimeout(this.tipTimer);
    this.tipTimer = window.setTimeout(() => {
      this.tipTimer = 0;
      this.showTip(tile);
    }, TIP_DELAY_MS);
  }

  private hideTipSoon(): void {
    if (this.tipTimer) window.clearTimeout(this.tipTimer);
    this.tipTimer = window.setTimeout(() => {
      this.tipTimer = 0;
      this.hideTip();
    }, 150);
  }

  /** Above the tile, or below it in the first row; kept inside the panel. */
  private showTip(tile: HTMLElement): void {
    const key = tile.getAttribute('data-pref') as TileKey;
    const text = STRINGS.help[key];
    if (!text) return;
    this.tip.textContent = text;
    this.tip.hidden = false;
    const box = this.tip.parentElement as HTMLElement;
    const width = this.tip.offsetWidth;
    const height = this.tip.offsetHeight;
    const centre = tile.offsetLeft + tile.offsetWidth / 2;
    const left = Math.max(
      8,
      Math.min(centre - width / 2, box.clientWidth - width - 8),
    );
    const above = tile.offsetTop - height - 8;
    const top = above >= 4 ? above : tile.offsetTop + tile.offsetHeight + 8;
    this.tip.style.left = `${Math.round(left)}px`;
    this.tip.style.top = `${Math.round(top)}px`;
  }

  private hideTip(): void {
    if (this.tipTimer) window.clearTimeout(this.tipTimer);
    this.tipTimer = 0;
    if (this.tip) this.tip.hidden = true;
  }

  // ---- statement dialog ----------------------------------------------------

  private openModal(): void {
    if (!this.statementText) return;
    if (!this.modalBody.firstChild) {
      this.modalBody.appendChild(renderStatement(document, this.statementText));
    }
    this.isModalOpen = true;
    // The panel steps aside while the statement is up, so only the dialog
    // shows over the dimmed page, and comes back when it closes.
    this.panel.hidden = true;
    this.layer.hidden = false;
    this.modalTitle.focus();
  }

  private closeModal(returnFocus = true): void {
    if (!this.isModalOpen) return;
    this.isModalOpen = false;
    this.layer.hidden = true;
    if (this.isOpen) this.panel.hidden = false;
    if (returnFocus) this.statementButton.focus();
  }

  /** Keeps Tab and Shift+Tab inside the dialog while it is open. */
  private trapTab(e: KeyboardEvent): void {
    const items = Array.prototype.slice.call(
      this.modal.querySelectorAll('button, a[href]'),
    ) as HTMLElement[];
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    const active = this.shadow.activeElement;
    if (e.shiftKey && (active === first || active === this.modalTitle)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  // ---- read aloud ----------------------------------------------------------

  /** No speech engine in this browser: the tile goes, and the player never shows. */
  private setupReader(): void {
    if (!speechSupported()) {
      const tileEl = this.shadow.querySelector('.switch[data-pref="speech"]');
      if (tileEl && tileEl.parentNode) tileEl.parentNode.removeChild(tileEl);
      return;
    }
    this.reader = new Reader({
      state: () => this.renderReader(),
      // Only the page is scrolled to; something pointed at is already on screen.
      block: (el) =>
        this.highlight(el, !!this.reader && this.reader.kind === 'page'),
      message: (text) => this.setReaderMessage(text),
      blocked: () => {
        this.waitingForClick = true;
        this.setReaderMessage(STRINGS.readClickToStart);
      },
    });

    // Pressing a button can clear the page's selection before "click" fires.
    this.readerPlay.addEventListener('pointerdown', () => {
      this.pressedSelection = selectionItems(document);
    });
    this.readerPlay.addEventListener('click', () => {
      const reader = this.reader;
      if (!reader) return;
      const selection = this.pressedSelection || selectionItems(document);
      this.pressedSelection = null;
      this.waitingForClick = false;
      if (selection) reader.read(selection);
      else if (reader.state === 'playing' && reader.kind === 'page')
        reader.pause();
      else if (reader.state === 'paused') reader.resume();
      else reader.read(pageItems(document));
    });
    this.readerStop.addEventListener('click', () => {
      this.readerPlay.focus();
      if (this.reader) this.reader.stop();
    });
    this.readerRate.addEventListener('click', () => {
      const i = SPEECH_RATES.indexOf(this.prefs.speechRate);
      const speechRate = SPEECH_RATES[(i + 1) % SPEECH_RATES.length];
      this.emit({ ...this.prefs, speechRate });
    });
    (
      this.shadow.querySelector('.reader-close') as HTMLElement
    ).addEventListener('click', () => {
      this.emit({ ...this.prefs, speech: false });
      // The player is gone; the launcher is the nearest thing to land on.
      this.launcher.focus();
    });
    // Some browsers keep talking after the visitor leaves the page.
    window.addEventListener('pagehide', () => window.speechSynthesis.cancel());
  }

  /**
   * Shows or hides the player. Turning it on from the panel reads the page
   * straight away. On a page that loads with it on, it tries to read too; a
   * browser that will not let a page talk before a click gets a "click
   * anywhere" message, and the first click starts it. Turning it on from
   * another tab only shows the player.
   */
  private setSpeech(on: boolean): void {
    const reader = this.reader;
    if (on === this.speechOn || !reader) return;
    this.speechOn = on;
    this.readerBar.hidden = !on;
    if (on) {
      reader.warm();
      document.addEventListener('click', this.onPageClick, true);
      document.addEventListener('keydown', this.onPageKey);
      document.addEventListener('selectionchange', this.onSelection);
      document.addEventListener('pointerover', this.onPoint);
      document.addEventListener('pointerout', this.onPointOut);
      this.onSelection();
      this.renderReader();
      if (this.startReading) {
        reader.read(pageItems(document));
      } else if (!this.mounted) {
        // Let the page finish laying out (fonts, late styles) first.
        window.setTimeout(() => {
          if (this.speechOn && reader.state === 'idle' && !this.waitingForClick)
            reader.read(pageItems(document), 0, { auto: true });
        }, 400);
      }
    } else {
      reader.stop();
      document.removeEventListener('click', this.onPageClick, true);
      document.removeEventListener('keydown', this.onPageKey);
      document.removeEventListener('selectionchange', this.onSelection);
      document.removeEventListener('pointerover', this.onPoint);
      document.removeEventListener('pointerout', this.onPointOut);
      this.onPointOut();
      this.waitingForClick = false;
      this.setReaderMessage('');
    }
    this.startReading = false;
  }

  private setReaderMessage(text: string): void {
    this.readerMsg.textContent = text;
    this.renderReader();
  }

  /** The Read button says what it will do: read the selection, pause, resume or read the page. */
  private renderReader(): void {
    const reader = this.reader;
    if (!reader) return;
    const state = reader.state;
    let label = STRINGS.readPage;
    let icon = PLAY_ICON;
    if (this.hasSelection) {
      label = STRINGS.readSelection;
    } else if (state === 'playing' && reader.kind === 'page') {
      label = STRINGS.readPause;
      icon = PAUSE_ICON;
    } else if (state === 'paused') {
      label = STRINGS.readResume;
    }
    (
      this.readerPlay.querySelector('.reader-play-icon') as HTMLElement
    ).innerHTML = icon;
    (
      this.readerPlay.querySelector('.reader-play-label') as HTMLElement
    ).textContent = label;
    if (state === 'idle' && this.shadow.activeElement === this.readerStop)
      this.readerPlay.focus();
    this.readerStop.disabled = state === 'idle';
    // How to use it, while it is not reading the page and has nothing else to say.
    this.readerHint.hidden =
      (state !== 'idle' && reader.kind === 'page') ||
      !!this.readerMsg.textContent;
  }

  private readonly onSelection = () => {
    const sel =
      typeof document.getSelection === 'function'
        ? document.getSelection()
        : null;
    const has = !!sel && !sel.isCollapsed && sel.toString().trim() !== '';
    if (has === this.hasSelection) return;
    this.hasSelection = has;
    this.renderReader();
  };

  /**
   * While read aloud is on, a click on a block of text reads from there.
   * Clicks on links, buttons and form fields do what they always do, and so
   * does a click that ends a text selection or closes the panel.
   */
  private readonly onPageClick = (event: MouseEvent) => {
    // A soft click for every click, in the page or the widget, while it is on.
    tick();
    if (this.swallowClick) {
      this.swallowClick = false;
      return;
    }
    const reader = this.reader;
    if (!reader || event.button !== 0) return;
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey)
      return;
    const path =
      typeof event.composedPath === 'function' ? event.composedPath() : [];
    if (path.indexOf(this.host) !== -1) return;
    const target = event.target as Element | null;
    if (!target || target.nodeType !== 1 || target.closest(INTERACTIVE)) return;
    if (this.hasSelection) return;
    const hit = itemsFrom(document, target);
    if (hit) {
      reader.read(hit.items, hit.start);
    } else if (this.waitingForClick) {
      // The click the browser was waiting for, on empty space: read the page.
      reader.read(pageItems(document));
    }
    if (this.waitingForClick) {
      this.waitingForClick = false;
      if (this.readerMsg.textContent === STRINGS.readClickToStart)
        this.setReaderMessage('');
    }
  };

  /**
   * Pointing at text, a link or an image for a moment reads it, unless the
   * page is being read (moving or scrolling the mouse would keep cutting it
   * off). Mouse only: a tap is a click.
   */
  private readonly onPoint = (event: PointerEvent) => {
    if (event.pointerType && event.pointerType !== 'mouse') return;
    if (this.pointTimer) window.clearTimeout(this.pointTimer);
    this.pointTimer = 0;
    const path =
      typeof event.composedPath === 'function' ? event.composedPath() : [];
    const target = event.target as Element | null;
    if (!target || target.nodeType !== 1 || path.indexOf(this.host) !== -1)
      return;
    this.pointTimer = window.setTimeout(() => {
      this.pointTimer = 0;
      const reader = this.reader;
      if (!reader || this.waitingForClick || !target.isConnected) return;
      if (reader.state === 'paused') return;
      if (reader.state === 'playing' && reader.kind === 'page') return;
      const item = pointItem(target);
      if (!item) {
        this.lastPointed = null;
        return;
      }
      if (item.el === this.lastPointed) return;
      this.lastPointed = item.el;
      reader.read([item], 0, { kind: 'point' });
    }, POINT_DWELL_MS);
  };

  private readonly onPointOut = (event?: PointerEvent) => {
    // Leaving the window (no related target) cancels a pending read.
    if (event && event.relatedTarget) return;
    if (this.pointTimer) window.clearTimeout(this.pointTimer);
    this.pointTimer = 0;
  };

  /** Esc anywhere on the page stops reading. Nothing else is taken from the page. */
  private readonly onPageKey = (event: KeyboardEvent) => {
    if (
      (event.key === 'Escape' || event.key === 'Esc') &&
      this.reader &&
      this.reader.state !== 'idle'
    )
      this.reader.stop();
  };

  /** Outlines the block being read and, when `follow`, keeps it on screen. */
  private highlight(el: HTMLElement | null, follow: boolean): void {
    if (this.reading && this.reading !== el)
      this.reading.removeAttribute(READING_ATTR);
    this.reading = el;
    if (!el) return;
    el.setAttribute(READING_ATTR, '');
    if (!follow) return;
    const box = el.getBoundingClientRect();
    const height = window.innerHeight || document.documentElement.clientHeight;
    // Room for a fixed site header above and the player below.
    if (box.top >= 80 && box.bottom <= height - READER - 40) return;
    const still = effectiveMotion(this.prefs, this.env.deviceReducesMotion);
    try {
      el.scrollIntoView({
        block: box.height > height * 0.6 ? 'start' : 'center',
        behavior: still ? 'auto' : 'smooth',
      });
    } catch (e) {
      el.scrollIntoView();
    }
  }

  // ---- reading guide -------------------------------------------------------

  private readonly moveGuide = (event: MouseEvent) => {
    this.guide.hidden = false;
    this.guide.style.transform = `translateY(${event.clientY}px)`;
  };

  private readonly hideGuide = () => {
    this.guide.hidden = true;
  };

  /** Follows the mouse only, so it never appears on touch-only devices. */
  private setGuide(on: boolean): void {
    if (on && !this.guideListening) {
      window.addEventListener('mousemove', this.moveGuide, { passive: true });
      document.documentElement.addEventListener('mouseleave', this.hideGuide);
      this.guideListening = true;
    } else if (!on && this.guideListening) {
      window.removeEventListener('mousemove', this.moveGuide);
      document.documentElement.removeEventListener(
        'mouseleave',
        this.hideGuide,
      );
      this.guideListening = false;
      this.guide.hidden = true;
    }
  }
}

/**
 * The statement link, if it is a web address: http(s), or a path on this site.
 * The server already refuses anything else; this is a second check on the page.
 */
function safeLink(raw: string): string {
  const value = raw.trim();
  if (!value) return '';
  try {
    const url = new URL(value, window.location.href);
    // The value as given (a site path stays a path), once its scheme checks out.
    return url.protocol === 'http:' || url.protocol === 'https:' ? value : '';
  } catch (e) {
    return '';
  }
}

// ---- the public chatbot ------------------------------------------------------

function parseJson(value: string | null): any {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch (e) {
    return null;
  }
}

/**
 * Which corner the public chatbot's launcher is in, or null when the page has
 * no chatbot. Reads the configuration footer.jsp renders onto the chatbot's
 * root (contract.ts, CHATBOT). A root without readable configuration still
 * means a chatbot, which then fetches its settings and defaults to the right.
 */
export function chatbotSide(doc: Document): 'left' | 'right' | null {
  const root = doc.getElementById(CHATBOT.rootId);
  if (!root) return null;
  const config = parseJson(root.getAttribute(CHATBOT.configAttr));
  const settings = config && config.payload && config.payload.clientSettings;
  const types = parseJson(root.getAttribute(CHATBOT.typesAttr));
  const positions =
    types && types.payload && types.payload.enums
      ? types.payload.enums.ChatbotPositionEnum
      : null;
  const left =
    positions && typeof positions.BottomLeft === 'number'
      ? positions.BottomLeft
      : CHATBOT.bottomLeft;
  return settings && settings.position === left ? 'left' : 'right';
}

// ---- markup ------------------------------------------------------------------

const tileCheck = `<span class="tile-check" aria-hidden="true">${CHECK_ICON}</span>`;

const tileInfo = `<span class="tile-info" aria-hidden="true">i</span>`;

function switchTile(key: SwitchKey, label: string): string {
  return (
    `<button type="button" class="tile switch" role="switch" aria-checked="false" data-pref="${key}" aria-describedby="ea11y-help-${key}">` +
    tileInfo +
    tileCheck +
    `<span class="tile-icon" aria-hidden="true">${SETTING_ICONS[key]}</span>` +
    `<span class="tile-label">${label}</span>` +
    `</button>`
  );
}

function cycleTile(key: CycleKey): string {
  let bars = '';
  for (let i = 0; i < stepCount(key); i++) bars += '<span></span>';
  return (
    `<button type="button" class="tile cycle" data-pref="${key}" aria-describedby="ea11y-help-${key}">` +
    tileInfo +
    tileCheck +
    `<span class="tile-icon" aria-hidden="true"></span>` +
    `<span class="tile-label">${STRINGS.cycles[key].name}</span>` +
    `<span class="steps" aria-hidden="true">${bars}</span>` +
    `</button>`
  );
}

/** "Ab" in the font the step uses, so the tile shows what you get. */
const FONT_FAMILIES = [
  'inherit',
  "'Edlio Atkinson Hyperlegible', Verdana, sans-serif",
  "'Edlio OpenDyslexic', 'Comic Sans MS', sans-serif",
];

function cycleIcon(key: CycleKey, prefs: Prefs): string {
  switch (key) {
    case 'textSize':
      return TEXT_SIZE_ICON;
    case 'font':
      return `<span class="glyph-text" style="font-family: ${FONT_FAMILIES[stepOf('font', prefs.font)]}">Ab</span>`;
    case 'align':
      // Off shows left-aligned lines: what the tile does, not a page.
      return ALIGN_ICONS[prefs.align === 'none' ? 'left' : prefs.align];
    case 'contrast':
      return CONTRAST_ICONS[prefs.contrast];
    default:
      return SATURATION_ICONS[prefs.saturation];
  }
}

/** Label, icon, bars and on state for a tile with steps. */
function drawCycle(tile: HTMLElement, key: CycleKey, prefs: Prefs): void {
  const step = stepOf(key, prefs[key]);
  const copy = STRINGS.cycles[key];
  tile.classList.toggle('on', step > 0);
  (tile.querySelector('.tile-label') as HTMLElement).textContent = step
    ? copy.steps[step - 1]
    : copy.name;
  const icon = tile.querySelector('.tile-icon') as HTMLElement;
  const markup = cycleIcon(key, prefs);
  if (icon.getAttribute('data-for') !== markup) {
    icon.innerHTML = markup;
    icon.setAttribute('data-for', markup);
  }
  const bars = tile.querySelectorAll('.steps > span');
  for (let i = 0; i < bars.length; i++)
    bars[i].classList.toggle('filled', i < step);
}

function tileFor(key: TileKey): string {
  if (isCycle(key)) return cycleTile(key);
  const label = key === 'speech' ? STRINGS.readAloud : STRINGS[key];
  return switchTile(key, label);
}

function template(): string {
  const s = STRINGS;
  return (
    `<div class="root">` +
    `<button type="button" class="launcher" aria-label="${s.launcher}" aria-expanded="false" aria-controls="ea11y-panel">` +
    `${LAUNCHER_ICON}<span class="dot" hidden></span></button>` +
    `<div class="reader" role="region" aria-label="${s.readAloud}" hidden>` +
    `<span class="reader-icon" aria-hidden="true">${SPEAKER_ICON}</span>` +
    `<button type="button" class="reader-play"><span class="reader-play-icon">${PLAY_ICON}</span><span class="reader-play-label">${s.readPage}</span></button>` +
    `<button type="button" class="reader-btn reader-stop" aria-label="${s.readStop}" disabled>${STOP_ICON}</button>` +
    `<button type="button" class="reader-btn reader-rate" aria-label="${s.readSpeed} 1\u00d7"><span class="rate-word">${s.readSpeed}</span> <span class="rate-value">1\u00d7</span></button>` +
    `<button type="button" class="reader-btn reader-close" aria-label="${s.readClose}">${CLOSE_ICON}</button>` +
    `<p class="reader-msg" role="status" aria-live="polite"></p>` +
    `<p class="reader-hint">${s.readHint}</p>` +
    `</div>` +
    `<div class="panel" id="ea11y-panel" role="dialog" aria-labelledby="ea11y-title" hidden>` +
    `<div class="header">` +
    `<span class="badge" aria-hidden="true">${HEADER_ICON}</span>` +
    `<h2 id="ea11y-title" tabindex="-1">${s.title}</h2>` +
    `<button type="button" class="close" aria-label="${s.close}">${CLOSE_ICON}</button>` +
    `</div>` +
    `<p class="intro">${s.intro}</p>` +
    `<div class="body"><div class="tiles">${TILE_ORDER.map(tileFor).join('')}</div>` +
    // Drawn once and moved to the tile in question; screen readers get the
    // same words as each tile's description, so the drawing is hidden.
    `<div class="tip" aria-hidden="true" hidden></div></div>` +
    `<div hidden>${TILE_ORDER.map((key) => `<span id="ea11y-help-${key}">${s.help[key]}</span>`).join('')}</div>` +
    `<div class="footer">` +
    `<div class="actions">` +
    `<button type="button" class="reset">${RESET_ICON}<span>${s.reset}</span></button>` +
    `<button type="button" class="statement statement-open" hidden>${STATEMENT_ICON}<span>${s.statement}</span></button>` +
    `<a class="statement statement-link" hidden>${STATEMENT_ICON}<span>${s.statement}</span></a>` +
    `</div>` +
    `<p class="brand"><span>${s.poweredBy}</span>` +
    `<a class="brand-link" href="${BRAND_URL}" target="_blank" rel="noopener">` +
    `<img class="logo" alt="${s.brandName}" width="54" height="18"><span class="sr-only"> ${s.newTab}</span></a>` +
    `<span>${s.brandProduct}</span></p>` +
    `</div>` +
    `<div class="sr-only" role="status" aria-live="polite"></div>` +
    `</div>` +
    `<div class="layer" hidden>` +
    `<div class="modal" role="dialog" aria-modal="true" aria-labelledby="ea11y-statement-title">` +
    `<div class="modal-head"><h2 id="ea11y-statement-title" tabindex="-1">${s.statement}</h2>` +
    `<button type="button" class="modal-close" aria-label="${s.statementClose}">${CLOSE_ICON}</button></div>` +
    `<div class="modal-body"></div>` +
    `</div>` +
    `</div>` +
    `<div class="guide" aria-hidden="true" hidden></div>` +
    `</div>`
  );
}
